import { useState, useMemo, useEffect, useCallback } from 'react';
import { ProductSearchMaterial } from '@productSearch/types';
import { checkMultipleMaterialsAvailability } from '@productSearch/utils';
import sortBy from 'lodash/sortBy';

import { getImageUrl } from './resultCard.utils';
import { SelectedColorVariants } from './types';
import { formatImageSource, getEventsMaterialProps } from '../../../helpers';
import { getIsPaintProductType, isUnavailable } from '../../../utils/helpers';

type UseResultCardProps = {
  product: ProductSearchMaterial;
  imageSize: number;
  selectedVariants: SelectedColorVariants | undefined;
};

export function useResultCard({
  product,
  imageSize,
  selectedVariants,
}: UseResultCardProps) {
  const [isVariantInStock, setIsVariantInStock] = useState<boolean | null>(
    null,
  );
  const isPaint = getIsPaintProductType(product);
  const imageUrl = useMemo(
    () => getImageUrl(product.thumbnailImageUrl, imageSize),
    [product, imageSize],
  );
  const analyticsProps = useMemo(() => {
    return getEventsMaterialProps(product);
  }, [product]);

  const { memoizedVariants, variantEntityId, variantImageUrl } = useMemo(() => {
    // TODO: refactor!
    const variants = product.colorVariants?.items;
    const rawImage = imageUrl?.split('?')?.[0];
    const newList = sortBy(variants || [], (obj) => obj.image_url !== rawImage);
    //

    const variantIdBasedOnImage = rawImage
      ? variants?.find((variant) => variant.image_url === rawImage)?.entity_id
      : null;

    /**
     * Determine the initial variant selection:
     * 1. First priority: Use _colorway_id if available (specific colorway)
     * 2. Second priority: Use the variant matching the current image (not ideal)
     * 3. Final fallback: Use the main product entityId
     */
    const dataVariantId =
      product?.originalMaterial?._colorway_id ??
      variantIdBasedOnImage ??
      product.entityId;

    /**
     * Check for user-selected variants:
     * If a variant was previously selected
     * for this product, prioritize it over the default variant selection.
     */
    const userSelectedVariantId = selectedVariants?.[product.entityId];
    const variantId = userSelectedVariantId || dataVariantId;

    const currentVariantObj = newList?.find(
      (item) => item.entity_id === variantId,
    );
    const variantImage =
      currentVariantObj?.image_url != null
        ? formatImageSource(currentVariantObj?.image_url, 420, false, 80)?.src
        : null;

    return {
      memoizedVariants: newList,
      variantEntityId: variantId,
      variantImageUrl: variantImage,
      userSelectedVariantId,
    };
  }, [product, imageUrl, selectedVariants]);

  /**
   * Check stock status for a variant, handling both unavailability checks
   * and async stock data fetching when needed.
   */
  const checkVariantStock = useCallback(
    async (entityId: string) => {
      if (isUnavailable(product, entityId)) {
        setIsVariantInStock(false);

        return;
      }

      try {
        const stockData = await checkMultipleMaterialsAvailability([entityId]);
        const inStock =
          stockData?.[0]?.qty !== null &&
          stockData?.[0]?.qty !== undefined &&
          Number(stockData[0].qty) > 0;

        setIsVariantInStock(inStock);
      } catch {
        setIsVariantInStock(null);
      }
    },
    [product],
  );

  // Check initial variant stock on mount and when variant changes
  useEffect(() => {
    if (isUnavailable(product, variantEntityId)) {
      setIsVariantInStock(false);
    }
  }, [variantEntityId, product]);

  return {
    isVariantInStock,
    isPaint,
    imageUrl,
    analyticsProps,
    variantEntityId,
    variantImageUrl,
    memoizedVariants,
    checkVariantStock,
  };
}
